import axios from 'axios';

const API_URL = '/v1';
let csrfToken = '';
let authRedirectPending = false;
let authGeneration = 0;

const readCookie = (name) => {
  if (typeof document === 'undefined') return '';
  const prefix = `${encodeURIComponent(name)}=`;
  const item = document.cookie.split('; ').find(value => value.startsWith(prefix));
  if (!item) return '';
  try {
    return decodeURIComponent(item.slice(prefix.length));
  } catch (_) {
    return item.slice(prefix.length);
  }
};

// 确保在DOM加载后获取CSRF token
if (typeof document !== 'undefined') {
  const csrfMetaTag = document.querySelector('meta[name="csrf-token"]');
  if (csrfMetaTag) {
    csrfToken = csrfMetaTag.getAttribute('content');
  }
}

const apiClient = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    'X-CSRF-Token': csrfToken,
  },
});

// CSRF tokens can be replaced by Turbo/full-page navigation. Read the current
// meta value for every mutating request instead of keeping a stale copy.
apiClient.interceptors.request.use(
  (config) => {
    config.qzAuthGeneration = authGeneration;
    if (typeof document !== 'undefined') {
      const currentToken = readCookie('qz_admin_csrf') ||
        document.querySelector('meta[name="csrf-token"]')?.content;
      if (currentToken) config.headers['X-CSRF-Token'] = currentToken;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 添加响应拦截器
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Keep every screen compatible with the API's historical `error` field.
    // Several older views only read `message`, which otherwise hides the real
    // cause behind a generic "操作失败" toast.
    const responseData = error.response?.data;
    if (responseData && typeof responseData === 'object' && responseData.error && !responseData.message) {
      responseData.message = responseData.error;
    }

    const isLoginRequest = error.config?.url === '/login';
    const requestGeneration = error.config?.qzAuthGeneration;
    const staleAuthRequest = requestGeneration !== undefined &&
      requestGeneration !== authGeneration;
    if (error.response && error.response.status === 401 && !isLoginRequest && !staleAuthRequest) {
      authGeneration += 1;
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem('user');
      }
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('auth-session-invalid'));
        const skipRedirect = error.config?.skipAuthRedirect;
        if (!skipRedirect && window.location.pathname !== '/login' && !authRedirectPending) {
          authRedirectPending = true;
          window.location.replace('/login');
        }
      }
    }
    return Promise.reject(error);
  }
);

const api = {
  storeList: () => apiClient.get('/store_admin'),
  storeUpload: data => apiClient.post('/store_admin/upload', data, {timeout: 180000}),
  storeUpdate: (uid, plugin) => apiClient.patch(`/store_admin/${encodeURIComponent(uid)}`, {plugin}),
  storeDelete: uid => apiClient.delete(`/store_admin/${encodeURIComponent(uid)}`),
  storeGrant: (uid, data) => apiClient.post(`/store_admin/${encodeURIComponent(uid)}/grant`, data),
  storeOrders: params => apiClient.get('/store_admin/orders', {params}),
  storeStatistics: params => apiClient.get('/store_admin/statistics', {params}),
  getViewerCloudAccounts: params => apiClient.get('/viewer_cloud_accounts', {params,timeout:25000}),
  getViewerCloudAccount: username => apiClient.get('/viewer_cloud_account', {params:{username},timeout:25000}),
  topUpViewerCloudAccount: data => apiClient.post('/viewer_cloud_account/top_up',data,{timeout:25000}),
  getWorkspaceOptions: () => apiClient.get('/admin_workspace/options', {timeout:20000}),
  getStudentActivity: username => apiClient.get('/admin_workspace/student_activity', {params:{username},timeout:20000}),
  previewAuthorization: params => apiClient.get('/admin_workspace/authorization_preview', {params,timeout:20000}),
  getRuntimeStatus: () => apiClient.get('/admin_workspace/runtime', {timeout:20000}),
  getPluginRuntimePolicy: () => apiClient.get('/plugin_runtime_policy'),
  updatePluginRuntimePolicy: (data) => apiClient.patch('/plugin_runtime_policy', data),
  getReleaseHistory: () => apiClient.get('/admin_workspace/releases', {timeout:20000}),
  getAnnouncementHistory: id => apiClient.get(`/admin_workspace/announcements/${id}/history`, {timeout:20000}),
  // 当前后台会话与工作台
  getMe: () => apiClient.get('/me', { skipAuthRedirect: true }),
  getDashboard: () => apiClient.get('/dashboard', {timeout:20000}),

  // 授权相关
  getAuths: (params = {}) => apiClient.get('/auths', { params }),
  getAuth: (id) => apiClient.get(`/auths/${id}`),
  createAuth: (data) => apiClient.post('/auths', { auth: data }),
  updateAuth: (id, data) => apiClient.put(`/auths/${id}`, { auth: data }),
  deleteAuth: (id) => apiClient.delete(`/auths/${id}`),
  toggleAuthStatus: (id) => apiClient.post(`/auths/${id}/toggle_status`),
  togglePluginStatus: (username, extensionName) => apiClient.post('/toggle_plugin_status', { username, extension_name: extensionName }),
  authLogout: (username) => apiClient.post('/admin_force_logout', { username }),
  resetUserPassword: (username) => apiClient.post('/reset_user_password', { username }),
  checkAuth: (data) => apiClient.post('/check_auth', data),
  verifyStatus: (data) => apiClient.post('/verify_status', data),
  getAuthsStats: () => apiClient.get('/auths_stats'),

  unbindDevice(username) {
    return apiClient.post('/unbind_device', { username });
  },
  unbindWechat: (username) => apiClient.post('/unbind_wechat', { username }),
  // AI 额度：超级管理员充值机构池；超级管理员分配给老师，老师分配给学员。
  topUpOrganizationWallet: (amountYuan, memo = '机构额度充值', requestId = `${Date.now()}-${Math.random().toString(36).slice(2)}`) =>
    apiClient.post('/ai_wallets/top_up', {
      amount_yuan: amountYuan,
      memo,
      request_id: requestId,
    }),
  transferAiWallet: (data) => apiClient.post('/ai_wallets/transfer', data),
  reclaimAiWallet: (data) => apiClient.post('/ai_wallets/reclaim', data),
  getAiWalletTransactions: () => apiClient.get('/ai_wallets/transactions'),

  // 柜体看图次数：超级管理员充值机构池，再分配给老师或学员；
  // 老师只能分配给本人负责的学员，学员端不提供充值入口。
  getViewerQuota: () => apiClient.get('/viewer_quota'),
  topUpViewerQuota: (count, memo = '管理员增加看图次数', requestId = `${Date.now()}-${Math.random().toString(36).slice(2)}`) =>
    apiClient.post('/viewer_quota/top_up', { count, memo, request_id: requestId }),
  transferViewerQuota: (data) => apiClient.post('/viewer_quota/transfer', data),
  reclaimViewerQuota: (data) => apiClient.post('/viewer_quota/reclaim', data),
  getViewerQuotaTransactions: () => apiClient.get('/viewer_quota/transactions'),
  getViewerDeliveries: (params = {}) => apiClient.get('/viewer_deliveries', { params }),
  getViewerDeliveryStats: () => apiClient.get('/viewer_deliveries/stats'),

  // 组织、老师管理员与学员归属
  getAdminUsers: (params = {}) => apiClient.get('/admin_users', { params }),
  createAdminUser: (data) => apiClient.post('/admin_users', { user: data }),
  updateAdminUser: (id, data) => apiClient.patch(`/admin_users/${id}`, { user: data }),
  deleteAdminUser: (id) => apiClient.delete(`/admin_users/${id}`),
  getStudentAccounts: (params = {}) => apiClient.get('/student_accounts', { params }),
  assignStudentAccount: (data) => apiClient.patch('/student_accounts/assign', data),

  // 学员级统一授权期限（月费、年费、永久）
  batchAuthorize: (username, durationType, packageType = 'flagship', fingerprint) => apiClient.post('/batch_authorize', {
    username,
    duration_type: durationType,
    package_type: packageType,
    preview_fingerprint: fingerprint,
  }),

  // 旧接口保留给历史客户端兼容
  batchEnableExperience: (username) => apiClient.post('/batch_enable_experience', { username }),

  batchEnablePermanent: (username) => apiClient.post('/batch_enable_permanent', { username }),

  // 👇 ============ 新增：激活码管理接口 ============ 👇
  getActivationCodes: (params = {}) => apiClient.get('/activation_codes', { params }),
  generateActivationCodes: (data) => apiClient.post('/generate_activation_codes', data),
  allocateActivationCode: (id, allocatedToUserId) => apiClient.patch(`/activation_codes/${id}/allocate`, { allocated_to_user_id: allocatedToUserId }),
  deleteActivationCode: (id) => apiClient.delete(`/activation_codes/${id}`),

  // 认证相关
  login: async (credentials) => {
    const response = await apiClient.post('/login', {
      username: credentials.username,
      password: credentials.password
    });
    authGeneration += 1;
    authRedirectPending = false;
    return response;
  },
  logout: async () => {
    const response = await apiClient.delete('/logout', { skipAuthRedirect: true });
    authGeneration += 1;
    return response;
  },

  // 发布中心
  getAnnouncements: () => apiClient.get('/admin_announcements'),
  withdrawVersion: id => apiClient.post(`/app_versions/${id}/withdraw`),
  deleteVersion: id => apiClient.delete(`/app_versions/${id}`),
  publishVersion: (formData, onUploadProgress) => apiClient.post('/publish_version', formData, {
    onUploadProgress,
  }),
  publishAnnouncement: (payload) => apiClient.post('/publish_announcement', payload),
  deleteAnnouncement: (id) => apiClient.delete(`/announcements/${id}`),
  updateAnnouncement: (id, payload) => apiClient.patch(`/announcements/${id}`, payload),

  // 验证记录相关
  getCheckRecords: (params = {}) => apiClient.get('/check_records', { params }),
  getVerifyRecords: (params = {}) => apiClient.get('/verify_records', { params }),

  // 登录记录相关
  getLoginRecords: (params = {}) => apiClient.get('/login_records', { params }),

  // 统一安全审计
  getSecurityAudits: (params = {}) => apiClient.get('/security_audits', { params }),

  // 系统相关
  createDatabaseBackup: () => apiClient.post('/database_backups'),
  getLatestDatabaseBackup: () => apiClient.get('/database_backups/latest'),
  getDatabaseBackup: (id) => apiClient.get(`/database_backups/${id}`),
};

export default api;
